import ReconnectingWebSocket from 'reconnecting-websocket'
import { authService } from './auth'

interface WebSocketOptions {
  connectionTimeout?: number
  maxReconnectionDelay?: number
  maxRetries?: number
}

class WebSocketService {
  private wsConnection: ReconnectingWebSocket | null = null
  private isConnected = false
  private isConnecting = false
  private reconnectAttempts = 0
  private maxRetries: number
  private connectionTimeout: number
  private maxReconnectionDelay: number
  private currentUrl: string | null = null

  constructor(options: WebSocketOptions = {}) {
    this.connectionTimeout = options.connectionTimeout || 5000
    this.maxReconnectionDelay = options.maxReconnectionDelay || 1000 * 60
    this.maxRetries = options.maxRetries || 20
  }

  /**
   * Connect to websocket. Prefer user JWT when available; otherwise fall back to shop JWT.
   * When using user JWT, shop_id is not needed; for shop JWT, shop_id is optional.
   */
  async listenNotification(jwtOverride?: string, _method?: 'store' | 'user', shopIdOverride?: number | null) {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      return this.wsConnection
    }

    const wsUrl = await this.getWebSocketUrl(jwtOverride, shopIdOverride)

    // If already connected to the same URL, don't reconnect
    if (this.wsConnection && this.currentUrl === wsUrl && this.isConnected) {
      return this.wsConnection
    }

    this.isConnecting = true

    try {
      // Close existing connection if URL changed
      if (this.wsConnection && this.currentUrl !== wsUrl) {
        this.wsConnection.close()
        this.wsConnection = null
      }

      // Only create new connection if we don't have one or URL changed
      if (!this.wsConnection) {
        this.currentUrl = wsUrl
        this.wsConnection = new ReconnectingWebSocket(wsUrl, undefined, {
          connectionTimeout: this.connectionTimeout,
          maxReconnectionDelay: this.maxReconnectionDelay,
          maxRetries: this.maxRetries,
        })

        this.wsConnection.addEventListener('open', this.onOpen.bind(this))
        this.wsConnection.addEventListener('close', this.onClose.bind(this))
        this.wsConnection.addEventListener('message', this.onMessage.bind(this))
        this.wsConnection.addEventListener('error', (event: any) => this.onError(event))
      }
    } finally {
      this.isConnecting = false
    }

    return this.wsConnection
  }

  private async getWebSocketUrl(jwtOverride?: string, shopIdOverride?: number | null): Promise<string> {
    const baseUrl = import.meta.env.VITE_WS_URL
    const hasUserToken = authService.isUserToken()
    const token = jwtOverride || authService.getToken()

    if (!token) {
      throw new Error('Unable to establish websocket: missing JWT token')
    }

    const shopId = typeof shopIdOverride === 'number' ? shopIdOverride : authService.getShopId()

    const params = new URLSearchParams()
    params.set('jwt', token)
    // Only include shop_id for shop-token connections; user sockets do not need it
    if (!hasUserToken && shopId !== null && shopId !== undefined) {
      params.set('shop_id', shopId.toString())
    }

    // Determine endpoint path: user sockets go to /users, shop sockets go to /socket
    const pathSuffix = hasUserToken ? '/users' : '/socket'

    if (!baseUrl) {
      return `ws://localhost:8080${pathSuffix}?${params.toString()}`
    }

    // Remove trailing slash and any existing /socket or /users path from base URL
    let sanitizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl

    // Remove existing /socket or /users if present to avoid duplication
    if (sanitizedBase.endsWith('/socket')) {
      sanitizedBase = sanitizedBase.slice(0, -7) // Remove '/socket'
    } else if (sanitizedBase.endsWith('/users')) {
      sanitizedBase = sanitizedBase.slice(0, -6) // Remove '/users'
    }

    return `${sanitizedBase}${pathSuffix}?${params.toString()}`
  }

  private onOpen() {
    this.isConnected = true
    this.reconnectAttempts = 0
    this.emitEvent('websocket:opened')
  }

  private onClose() {
    this.isConnected = false
    this.emitEvent('websocket:closed')
  }

  private onMessage(event: MessageEvent) {
    try {
      const data = JSON.parse(event.data)
      this.emitEvent('websocket:message', data)
    } catch (error) {
      this.emitEvent('websocket:message', event.data)
    }
  }

  private onError(error: ErrorEvent) {
    this.emitEvent('websocket:error', error)
  }

  private emitEvent(eventName: string, data?: any) {
    const customEvent = new CustomEvent(eventName, { detail: data })
    window.dispatchEvent(customEvent)
  }

  disconnect() {
    if (this.wsConnection) {
      this.wsConnection.close()
      this.wsConnection = null
      this.isConnected = false
      this.isConnecting = false
      this.currentUrl = null
    }
  }

  send(data: any) {
    if (this.wsConnection && this.isConnected) {
      this.wsConnection.send(JSON.stringify(data))
    }
  }

  get connectionStatus(): string {
    if (this.isConnected) return 'connected'
    if (this.isConnecting || (this.wsConnection && this.wsConnection.readyState === WebSocket.CONNECTING))
      return 'connecting'
    if (this.wsConnection && this.wsConnection.readyState === WebSocket.CLOSED) return 'disconnected'
    return 'disconnected'
  }
}

export const websocketService = new WebSocketService()
export default websocketService
